import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import {
  AgeRange,
  AgeRangeCatalog,
  labelToRange,
  rangeToLabel,
} from '../../utils/ageRanges';

interface AgeRangeSelectProps {
  value: AgeRange;
  onChange: (range: AgeRange, label: string) => void;
  label?: string;
  helperText?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  showCustomOption?: boolean;
  customLabel?: string;
  'data-testid'?: string;
}

const AgeRangeSelect: React.FC<AgeRangeSelectProps> = ({
  value,
  onChange,
  label,
  helperText,
  placeholder = 'Select age range',
  isDisabled = false,
  isRequired = false,
  error,
  size = 'md',
  width = '100%',
  minWidth = '200px',
  maxWidth,
  showCustomOption = false,
  customLabel,
  'data-testid': testId,
}) => {
  const selectedLabel = rangeToLabel(value);
  const matched = AgeRangeCatalog.some(
    (item) => item.label === selectedLabel
  );

  // If no valid selection, show empty value to display placeholder
  const selectValue = matched ? selectedLabel : '';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const label = e.target.value;
    try {
      const range = labelToRange(label);
      onChange(range, label);
    } catch (error) {
      console.warn('Error parsing age range:', error);
    }
  };

  return (
    <FormControl
      isInvalid={!!error}
      isRequired={isRequired}
      isDisabled={isDisabled}
      data-testid={testId}
    >
      {label && (
        <FormLabel
          fontSize="sm"
          fontWeight="600"
          color="gray.700"
          mb="1"
        >
          {label}
          {isRequired && (
            <Text as="span" color="red.500" ml="1">
              *
            </Text>
          )}
        </FormLabel>
      )}

      {helperText && (
        <Text fontSize="xs" color="gray.500" mb="2" lineHeight="1.4">
          {helperText}
        </Text>
      )}

      <Box position="relative">
        <Select
          value={selectValue}
          onChange={handleChange}
          placeholder={placeholder}
          isDisabled={isDisabled}
          size={size}
          width={width}
          minWidth={minWidth}
          maxWidth={maxWidth}
          bg="white"
          borderColor="gray.300"
          _hover={{
            borderColor: 'gray.400',
          }}
          _focus={{
            borderColor: 'blue.500',
            boxShadow: '0 0 0 1px #3182ce',
          }}
          _disabled={{
            bg: 'gray.50',
            color: 'gray.500',
            cursor: 'not-allowed',
          }}
          _invalid={{
            borderColor: 'red.500',
          }}
        >
          {/* Show custom option if provided and not matched */}
          {showCustomOption && !matched && customLabel && (
            <option value={selectedLabel} disabled>
              {customLabel}
            </option>
          )}

          {/* Show unmatched current value as disabled option */}
          {!matched && !showCustomOption && selectedLabel && (
            <option value={selectedLabel} disabled>
              {selectedLabel}
            </option>
          )}

          {/* Age range catalog options */}
          {AgeRangeCatalog.map((item) => (
            <option
              key={item.label}
              value={item.label}
              style={{
                padding: '8px 12px',
                fontSize: '14px',
              }}
            >
              {item.label}
            </option>
          ))}
        </Select>
      </Box>

      {error && (
        <FormErrorMessage fontSize="xs" mt="1">
          {error}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default AgeRangeSelect;
